/**
 * Flag conditions — the operator enum and the evaluator that honours it.
 *
 * `FLAG_CONDITION_OPERATORS` is the single source of truth: every write path that
 * persists a `flag_conditions` array validates against it, every reader and picker
 * consumes it, and `evaluateFlagConditions` honours exactly its members. That
 * coupling is the point. An operator a write path accepts but the evaluator does not
 * honour produces a condition that reads as configured everywhere — the agent form,
 * `wayai pull`, the stored column — and silently never fires, with nothing to tell
 * the operator why.
 *
 * Lives in `@wayai/core` (not the backend) because the contracts that gate those
 * write paths, the web picker, and the backend evaluator all need the same list, and
 * core is the only package all three can import.
 */

/**
 * Every operator a flag condition may carry. Ordering operators compare numerically;
 * see `evaluateFlagConditions`.
 */
export const FLAG_CONDITION_OPERATORS = ['=', '!=', '>=', '<=', '>', '<'] as const;

export type FlagConditionOperator = (typeof FLAG_CONDITION_OPERATORS)[number];

/** The subset that compares by magnitude, derived so it cannot drift from the enum. */
export type FlagConditionOrderingOperator = Exclude<FlagConditionOperator, '=' | '!='>;

/**
 * One condition row. OR semantics across an array — any match flags.
 *
 * `value` admits `boolean` because the CI write path persists whatever the YAML
 * carries (`operator: "="`, `value: false` on a boolean evaluation variable is
 * ordinary config) and the evaluator honours it through `String()`. Narrowing it here
 * would reject on the REST write path exactly what a `wayai push` stores, so a hub
 * configured by one surface could not be saved from the other.
 */
export interface FlagCondition {
  variable: string;
  operator: FlagConditionOperator;
  value: string | number | boolean;
}

/**
 * A resolved conversation variable, as the analytics layer shapes it. Structurally
 * the backend's ClickHouse `CustomVariable` row — declared here so the evaluator
 * stays free of any backend import.
 */
export interface FlagConditionVariable {
  variable_name: string;
  variable_type: 'numeric' | 'categorical' | 'text';
  value_numeric: number | null;
  value_categorical: string | null;
  value_text: string | null;
}

/**
 * Coerce to a finite number, or null when the value is not numeric.
 *
 * `Number()` alone is not enough: it maps `''`, `'   '`, `null` and `[]` to 0, which
 * would make an empty variable satisfy `>= 0`. Non-finite results (`NaN`, `Infinity`)
 * are not orderable either, so they read as non-numeric too.
 */
function toNumeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Evaluate flag conditions against system metrics and custom variables.
 * Returns true if any condition matches (OR logic).
 *
 * The per-condition work is `evaluateFlagCondition` and the variable lookup is
 * `buildFlagConditionLookup`, both exported below: this function is the OR over
 * them, and a caller needing other quantification composes the same two pieces
 * rather than restating the comparison.
 *
 * `=` / `!=` compare stringified values — unchanged, and deliberately so: they are
 * how every existing hub's categorical conditions (`goal_achieved = no`) are written,
 * and numeric coercion there would silently change what `3.0 = 3` means on rows
 * already stored.
 *
 * The four ordering operators coerce both sides with `Number()` and fall back to
 * string comparison only when BOTH sides are non-numeric (so `tier > "gold"` is a
 * lexicographic comparison, while a numeric bar against a non-numeric variable simply
 * does not match — fail closed rather than compare a number against NaN).
 */
export function evaluateFlagConditions(
  conditions: FlagCondition[] | null,
  systemMetrics: Record<string, unknown>,
  customVariables: readonly FlagConditionVariable[],
): boolean {
  if (!conditions || conditions.length === 0) return false;
  const lookup = buildFlagConditionLookup(customVariables);
  return conditions.some((condition) => evaluateFlagCondition(condition, systemMetrics, lookup));
}

/**
 * The value a variable row carries: the column its own type designates.
 *
 * Exported because a caller that MAPS a variable (rather than comparing it) has
 * to read the same column the comparison reads. Two copies of this switch drift
 * the moment a `variable_type` is added, and the drift is silent: the condition
 * matches on one column while the mapped value comes from another.
 */
export function resolveFlagConditionVariable(variable: FlagConditionVariable): string | number | null {
  if (variable.variable_type === 'numeric') return variable.value_numeric;
  if (variable.variable_type === 'categorical') return variable.value_categorical;
  return variable.value_text;
}

/**
 * Name → value over a whole variable list, built once per evaluation.
 *
 * A repeated name keeps its LAST row, which is what building a map in list order
 * has always meant here — so a caller sharing this lookup resolves a duplicate to
 * the same row the comparison used.
 */
export function buildFlagConditionLookup(
  customVariables: readonly FlagConditionVariable[],
): Map<string, string | number | null> {
  const lookup = new Map<string, string | number | null>();
  for (const variable of customVariables) {
    lookup.set(variable.variable_name, resolveFlagConditionVariable(variable));
  }
  return lookup;
}

/**
 * One condition against system metrics and a prebuilt lookup.
 *
 * Exported so a caller that needs AND across conditions can have it without a
 * second comparison implementation: `evaluateFlagConditions` ORs these, and a
 * caller wanting AND runs every one of them. The operator switch lives here and
 * nowhere else.
 *
 * A variable neither side resolves, or one resolving to null, does not match —
 * fail closed rather than compare against nothing.
 */
export function evaluateFlagCondition(
  condition: FlagCondition,
  systemMetrics: Record<string, unknown>,
  lookup: ReadonlyMap<string, string | number | null>,
): boolean {
  // Resolve variable value: system metrics first, then custom variables
  let resolvedValue: unknown;
  if (condition.variable in systemMetrics) {
    resolvedValue = systemMetrics[condition.variable];
  } else if (lookup.has(condition.variable)) {
    resolvedValue = lookup.get(condition.variable);
  } else {
    return false;
  }

  if (resolvedValue === null || resolvedValue === undefined) return false;

  switch (condition.operator) {
    case '=':
      return String(resolvedValue) === String(condition.value);
    case '!=':
      return String(resolvedValue) !== String(condition.value);
    case '>=':
    case '<=':
    case '>':
    case '<':
      return compareOrdered(condition.operator, resolvedValue, condition.value);
    default: {
      // Exhaustive over FLAG_CONDITION_OPERATORS: adding a member without an arm
      // here fails typecheck, so an operator can never again reach a write path
      // that persists it and an evaluator that silently ignores it.
      const unreachable: never = condition.operator;
      void unreachable;
      return false;
    }
  }
}

/** `>=` / `<=` / `>` / `<` against a resolved variable value. */
function compareOrdered(
  operator: FlagConditionOrderingOperator,
  resolvedValue: unknown,
  conditionValue: FlagCondition['value'],
): boolean {
  const left = toNumeric(resolvedValue);
  const right = toNumeric(conditionValue);

  // Exactly one side numeric → no match; both non-numeric → lexicographic. Picking
  // the operand pair here rather than per operator keeps ONE switch, so a new
  // ordering operator has a single arm to add.
  const bothNumeric = left !== null && right !== null;
  if (!bothNumeric && (left !== null || right !== null)) return false;

  const a = bothNumeric ? (left as number) : String(resolvedValue);
  const b = bothNumeric ? (right as number) : String(conditionValue);

  switch (operator) {
    case '>=': return a >= b;
    case '<=': return a <= b;
    case '>': return a > b;
    case '<': return a < b;
  }
}
